import { APEX_SESSION_ENDPOINT_URL } from "@/lib/apexSessionEndpoint";

/**
 * KB-3270 — clear the apex (`kidsbooked.com`) `__session` cookie on a
 * user-initiated pro sign-out. This is the INVERSE of KB-3235's
 * `apexSessionMint.ts`: there a pro sign-in projects a fresh session OUT to apex;
 * here a pro sign-out takes that same session away, so a director who logs out of
 * pro on a shared front-desk device is not left signed in on `kidsbooked.com` for
 * the next person to inherit.
 *
 * It calls `DELETE` on the SAME endpoint the mint POSTs to (both import
 * `APEX_SESSION_ENDPOINT_URL` from the dependency-free leaf `apexSessionEndpoint.ts`,
 * so the URL lives in one place). The
 * endpoint clears the cookie and the cached session entry, is already CORS-wired
 * for pro's origin (KB-3227 / KB-187), and already lists `DELETE` in its
 * `Access-Control-Allow-Methods` (verified live against prod: `OPTIONS` returns
 * 204 with `allow-methods: POST, DELETE, OPTIONS`). Authentication is by the
 * browser's own cookie (`credentials: "include"`), so — unlike the mint — there is
 * no ID token to fetch and no request body.
 *
 * ## Awaited, bounded, and non-fatal — the deliberate difference from the mint
 *
 * The mint is fire-and-forget: it enhances a sign-in that already succeeded, so a
 * failure must neither fail nor delay it. The revoke is the security-relevant
 * half, and its two contracts pull in opposite directions:
 *
 *  - It is AWAITED by the caller, so the apex cookie is actually gone before the
 *    user is told they are logged out. A logout that reports success while the
 *    consumer-site session survives is the exact shared-device state this ticket
 *    exists to prevent.
 *  - But a failure must NEVER leave the user stuck signed in. So this function,
 *    like the mint, NEVER throws — every failure (a rejected fetch, a non-2xx
 *    response, the abort below) resolves to an `ApexRevokeOutcome` and a single
 *    `console.warn`. The caller awaits the outcome and then completes its local
 *    sign-out regardless of which value came back. The fetch is bounded
 *    (`AbortController` + `APEX_SESSION_REVOKE_BUDGET_MS`) so a hung apex endpoint
 *    cannot delay the local sign-out past the budget.
 *
 * Both halves are pinned by `apexSessionRevoke.test.ts` (the never-throws /
 * outcome-union / bounded-abort properties) and the awaited-but-non-fatal wiring
 * by `AuthContext`'s sign-out spec.
 *
 * ## Not `/api/auth/logout` — that is a different, heavier act (AC scope)
 *
 * `DELETE /api/auth/session` ends the session on THIS browser only.
 * `/api/auth/logout` runs `revokeRefreshTokens`, signing the director out of every
 * device including their phone. A user clicking Log out on a shared iPad expects
 * the former, not the latter, so this helper deliberately uses `DELETE` on the
 * session endpoint. See the marketing `session.ts` `DELETE` handler's own comment.
 */

/**
 * The apex session-cookie endpoint. Imported from the dependency-free leaf
 * `apexSessionEndpoint.ts` — the SAME source the mint uses — so the mint and
 * revoke cannot drift onto different URLs (two verbs on one resource) WITHOUT
 * coupling this module's evaluation to the mockable `apexSessionMint` (KB-3270:
 * reading the URL from `apexSessionMint` broke every suite that partially mocked
 * it and rendered `AuthProvider` — see `apexSessionEndpoint.ts`).
 */
export const APEX_SESSION_REVOKE_URL = APEX_SESSION_ENDPOINT_URL;

/**
 * How long to wait on the apex revoke before aborting. Unlike the mint's budget
 * (which bounds only a leaked request, since the mint is un-awaited), this budget
 * bounds the user-visible sign-out: the caller awaits the revoke, so a hung apex
 * endpoint would otherwise delay the local sign-out indefinitely. Mirrors
 * `APEX_SESSION_MINT_BUDGET_MS`.
 */
export const APEX_SESSION_REVOKE_BUDGET_MS = 5000;

/**
 * What the revoke attempt resolved to. `revoked` means the endpoint accepted the
 * `DELETE` (the apex `__session` cookie is cleared). `rejected` is a
 * reached-but-refused endpoint (non-2xx). `error` is anything that threw: a
 * network/abort failure. Kept distinct so tests — and any future caller that
 * wants to surface a failure — can tell "the endpoint said no" from "we never
 * reached it". All three are non-fatal; the caller completes its local sign-out
 * regardless.
 */
export type ApexRevokeOutcome = "revoked" | "rejected" | "error";

/**
 * Clear the apex `__session` cookie for the current browser. Awaited by the
 * sign-out path but never throws and never blocks past the budget — see the
 * module docblock for why both are load-bearing.
 */
export async function revokeApexSession(): Promise<ApexRevokeOutcome> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), APEX_SESSION_REVOKE_BUDGET_MS);
  try {
    const res = await fetch(APEX_SESSION_REVOKE_URL, {
      method: "DELETE",
      credentials: "include",
      signal: controller.signal,
    });
    if (!res.ok) {
      // Reached the endpoint and it refused. Warn (not error, to stay out of
      // Sentry noise) — the local pro sign-out proceeds regardless.
      console.warn(`Apex session revoke: endpoint returned ${res.status}`);
      return "rejected";
    }
    return "revoked";
  } catch (revokeError) {
    // Network failure or abort on the budget. Swallowed deliberately: a failed
    // apex revoke must never leave the user stuck signed in locally (AC-2).
    console.warn("Apex session revoke failed:", revokeError);
    return "error";
  } finally {
    clearTimeout(timer);
  }
}
